import { ApiError } from "@/api/generated/core/ApiError";

export interface NormalizedApiError {
    message: string;
    fieldErrors: Record<string, string[]>;
    status?: number;
}

interface ApiErrorPayload {
    message?: string | null;
    fieldErrors?: Record<string, string[] | null> | null;
}

function normalizeFieldErrors(payload?: Record<string, string[] | null> | null): Record<string, string[]> {
    if (!payload) {
        return {};
    }

    const normalized: Record<string, string[]> = {};
    for (const [field, messages] of Object.entries(payload)) {
        if (!messages || messages.length === 0) {
            continue;
        }

        normalized[field] = messages.filter((message): message is string => typeof message === "string" && message.length > 0);
    }

    return normalized;
}

function isObject(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null;
}

function readPayload(value: unknown): ApiErrorPayload | null {
    if (!isObject(value)) {
        return null;
    }

    const message = typeof value.message === "string" ? value.message : null;
    const fieldErrors = isObject(value.fieldErrors)
        ? value.fieldErrors as Record<string, string[] | null>
        : null;

    return {
        message,
        fieldErrors
    };
}

export function normalizeApiError(error: unknown, fallbackMessage: string): NormalizedApiError {
    if (error instanceof ApiError) {
        const payload = readPayload(error.body);
        return {
            message: payload?.message || error.message || fallbackMessage,
            fieldErrors: normalizeFieldErrors(payload?.fieldErrors),
            status: error.status
        };
    }

    const payload = readPayload(error);
    if (payload) {
        return {
            message: payload.message || fallbackMessage,
            fieldErrors: normalizeFieldErrors(payload.fieldErrors)
        };
    }

    if (error instanceof Error) {
        return {
            message: error.message || fallbackMessage,
            fieldErrors: {}
        };
    }

    return {
        message: fallbackMessage,
        fieldErrors: {}
    };
}

export function mapFieldErrors<TField extends string>(
    fieldErrors: Record<string, string[]>,
    mapping: Partial<Record<TField, string[]>>
): Partial<Record<TField, string[]>> {
    const normalizedKeyMap = Object.fromEntries(
        Object.entries(fieldErrors).map(([key, value]) => [key.toLowerCase(), value])
    );

    const result: Partial<Record<TField, string[]>> = {};

    for (const [fieldName, aliases] of Object.entries(mapping) as Array<[TField, string[]]>) {
        const matched = aliases
            .map(alias => normalizedKeyMap[alias.toLowerCase()])
            .find(candidate => candidate && candidate.length > 0);

        if (matched) {
            result[fieldName] = matched;
        }
    }

    return result;
}
